import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAllUsers, 
  createUser, 
  updateUser, 
  deleteUser, 
  getUserStats, 
  getAvailableRoles 
} from '../../api/Admin/admin.api';
import Select from 'react-select';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import { getAllCompany } from '../../api/company/company.api.js';
import { getAllFiles } from '../../api/files/files.api.js';
import './Adduser.css';

const INITIAL_USER_STATE = {
  displayName: '',
  username: '',
  password: '',
  role: 'user',
  isActive: true,
  isSuperAdmin: false,
  companyAccess: [],
  locationAccess: [],
  mdsAccess: []
};

const normalizeLocationAccessEntry = (entry) => {
  if (!entry) {
    return null;
  }

  if (typeof entry === 'string') {
    return {
      companyId: null,
      location: entry
    };
  }

  const location =
    entry.location ??
    entry.locationName ??
    entry.name ??
    entry.label ??
    entry.value ??
    null;

  if (!location) {
    return null;
  }

  const companyId =
    entry.companyId ??
    entry.company ??
    entry.company_id ??
    entry.companyID ??
    null;

  return {
    companyId: companyId ?? null,
    location
  };
};

const normalizeTextValue = (value) => {
  if (value == null) {
    return '';
  }

  return String(value).trim().toLowerCase();
};

const normalizeAccessIdentifier = (value) => {
  const normalized = normalizeTextValue(value);
  return normalized.length ? normalized : null;
};

const buildLocationKey = (companyId, location) => {
  const normalizedLocation = normalizeTextValue(location);

  if (!normalizedLocation) {
    return '';
  }

  const normalizedCompanyId = normalizeAccessIdentifier(companyId) ?? '';

  return normalizedCompanyId ? `${normalizedCompanyId}::${normalizedLocation}` : normalizedLocation;
};

const AddUser = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [newUser, setNewUser] = useState({ ...INITIAL_USER_STATE });
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyLookup, setCompanyLookup] = useState({});
  const [locationOptions, setLocationOptions] = useState([]);
  const [mdsOptions, setMdsOptions] = useState([]);
  const [accessOptionsLoading, setAccessOptionsLoading] = useState(false);

  const selectedCompanyIdSet = useMemo(() => {
    const idSet = new Set();

    if (!Array.isArray(newUser.companyAccess)) {
      return idSet;
    }

    newUser.companyAccess.forEach(companyId => {
      if (companyId != null) {
        idSet.add(String(companyId));
      }
    });

    return idSet;
  }, [newUser.companyAccess]);

  const availableLocationOptions = useMemo(() => {
    if (!Array.isArray(locationOptions)) {
      return [];
    }

    if (!selectedCompanyIdSet.size) {
      return [];
    }

    return locationOptions.filter(option => {
      if (!option || option.companyId == null) {
        return false;
      }

      return selectedCompanyIdSet.has(String(option.companyId));
    });
  }, [locationOptions, selectedCompanyIdSet]);

  const selectedLocationKeySet = useMemo(() => {
    const keySet = new Set();

    if (!Array.isArray(newUser.locationAccess)) {
      return keySet;
    }

    newUser.locationAccess.forEach(access => {
      if (!access || !access.location) {
        return;
      }

      const companyId = access.companyId != null ? String(access.companyId) : null;
      const key = buildLocationKey(companyId, access.location);

      if (key) {
        keySet.add(key);
      }
    });

    return keySet;
  }, [newUser.locationAccess]);

  const mdsLookup = useMemo(() => {
    const lookup = new Map();

    if (!Array.isArray(mdsOptions)) {
      return lookup;
    }

    mdsOptions.forEach(option => {
      if (!option || !option.value) {
        return;
      }

      lookup.set(option.value, option);
    });

    return lookup;
  }, [mdsOptions]);

  const availableMdsOptions = useMemo(() => {
    if (!Array.isArray(mdsOptions)) {
      return [];
    }

    if (!selectedLocationKeySet.size) {
      return [];
    }

    return mdsOptions.filter(option => {
      if (!option || !option.location) {
        return false;
      }

      const companyId = option.companyId != null ? String(option.companyId) : null;
      const key = buildLocationKey(companyId, option.location);

      return selectedLocationKeySet.has(key);
    });
  }, [mdsOptions, selectedLocationKeySet]);

  useEffect(() => {
    fetchData();
    fetchAccessOptions();
  }, []);

  const fetchAccessOptions = async () => {
    try {
      setAccessOptionsLoading(true);
      const [companyRes, filesRes] = await Promise.all([
        getAllCompany(),
        getAllFiles()
      ]);

      let normalizedCompanyLookup = {};
      let companyAccumulatorResult = null;

      if (companyRes && !companyRes.error) {
        const companies = Array.isArray(companyRes.data?.data) ? companyRes.data.data : [];

        const companyAccumulator = companies.reduce(
          (acc, company) => {
            const idCandidate =
              company._id ??
              company.id ??
              company.companyId ??
              company.company_id ??
              company.value ??
              company.slug ??
              company.name ??
              company.label ??
              null;

            const nameCandidate =
              company.companyName ??
              company.name ??
              company.label ??
              company.displayName ??
              company.title ??
              idCandidate;

            if (!idCandidate || !nameCandidate) {
              return acc;
            }

            const normalizedId = String(idCandidate);
            const normalizedLabel = String(nameCandidate);

            acc.options.push({ value: normalizedId, label: normalizedLabel });
            acc.lookup[normalizedId] = { label: normalizedLabel, raw: company };

            const companyLocations = Array.isArray(company.locations) ? company.locations : [];

            companyLocations.forEach(locationEntry => {
              const locationName =
                locationEntry?.locationName ??
                locationEntry?.location ??
                locationEntry?.name ??
                locationEntry?.label ??
                locationEntry?.value ??
                null;

              if (!locationName) {
                return;
              }

              const normalizedLocation = String(locationName).trim();

              if (!normalizedLocation) {
                return;
              }

              const optionValue = buildLocationKey(normalizedId, normalizedLocation);

              if (acc.seenLocationKeys.has(optionValue)) {
                return;
              }

              acc.seenLocationKeys.add(optionValue);

              acc.locationOptions.push({
                value: optionValue,
                label: normalizedLabel
                  ? `${normalizedLocation} (${normalizedLabel})`
                  : normalizedLocation,
                companyId: normalizedId,
                location: normalizedLocation,
                locationId:
                  locationEntry?.locationId ??
                  locationEntry?.location_id ??
                  locationEntry?.id ??
                  locationEntry?._id ??
                  null
              });
            });

            return acc;
          },
          { options: [], lookup: {}, locationOptions: [], seenLocationKeys: new Set() }
        );

        companyAccumulatorResult = companyAccumulator;
        setCompanyOptions(companyAccumulator.options);
        setCompanyLookup(companyAccumulator.lookup);
        setLocationOptions(companyAccumulator.locationOptions);
        normalizedCompanyLookup = companyAccumulator.lookup;
      } else {
        setLocationOptions([]);
      }

      const getCompanyOptionsForLookup = () =>
        companyAccumulatorResult?.options?.length
          ? companyAccumulatorResult.options
          : companyOptions;

      const normalizeToken = (value) => {
        if (value == null) {
          return '';
        }

        if (typeof value === 'object') {
          const candidate =
            value._id ??
            value.id ??
            value.companyId ??
            value.company_id ??
            value.value ??
            value.slug ??
            value.name ??
            value.company ??
            value.companyName ??
            value.label ??
            value.title ??
            '';

          return normalizeToken(candidate);
        }

        return String(value).trim();
      };

      const normalizeKey = (value) => {
        const token = normalizeToken(value);
        return token ? token.toLowerCase() : '';
      };

      const resolveCompanyIdentifierForLocations = (candidateValue, fallbackLabel) => {
        const companyOptionsForLookup = getCompanyOptionsForLookup();
        const normalizedCandidate = normalizeToken(candidateValue);
        const normalizedCandidateKey = normalizeKey(candidateValue);

        if (normalizedCandidate) {
          if (normalizedCompanyLookup[normalizedCandidate] || companyLookup[normalizedCandidate]) {
            return normalizedCandidate;
          }

          const candidateMatch = companyOptionsForLookup.find(
            option => option.value === normalizedCandidate
          );

          if (candidateMatch) {
            return candidateMatch.value;
          }

          const candidateMatchCaseInsensitive = companyOptionsForLookup.find(option =>
            normalizeKey(option.value) === normalizedCandidateKey
          );

          if (candidateMatchCaseInsensitive) {
            return candidateMatchCaseInsensitive.value;
          }
        }

        const normalizedFallback = normalizeToken(fallbackLabel);
        const normalizedFallbackKey = normalizeKey(fallbackLabel);

        if (normalizedFallback) {
          const fallbackMatch = companyOptionsForLookup.find(
            option =>
              option.label === normalizedFallback ||
              option.value === normalizedFallback
          );

          if (fallbackMatch) {
            return fallbackMatch.value;
          }

          const fallbackMatchCaseInsensitive = companyOptionsForLookup.find(option =>
            normalizeKey(option.label) === normalizedFallbackKey ||
            normalizeKey(option.value) === normalizedFallbackKey
          );

          if (fallbackMatchCaseInsensitive) {
            return fallbackMatchCaseInsensitive.value;
          }
        }

        return null;
      };

      if (filesRes && !filesRes.error) {
        const files = Array.isArray(filesRes.data?.data) ? filesRes.data.data : [];
        const uniqueMds = [];
        const seen = new Set();

        const lookupForLocations =
          Object.keys(normalizedCompanyLookup).length > 0 ? normalizedCompanyLookup : companyLookup;

        files.forEach(file => {
          if (!file) {
            return;
          }

          const rawMds =
            file.mdsNumber ??
            file.mds ??
            file.mdsId ??
            file.mds_code ??
            file?.metadata?.mdsNumber ??
            file?.metadata?.mds ??
            null;

          const normalizedMds = rawMds != null ? String(rawMds).trim() : '';

          if (!normalizedMds) {
            return;
          }

          const fallbackCompanyLabel =
            file.companyName ??
            file.companyLabel ??
            file.company ??
            file?.metadata?.companyName ??
            file?.metadata?.company ??
            '';

          const resolvedCompanyId = resolveCompanyIdentifierForLocations(
            file.companyId ??
              file.company ??
              file.company_id ??
              file.companyID ??
              file?.metadata?.companyId ??
              file?.metadata?.companyID ??
              null,
            fallbackCompanyLabel
          );

          const resolvedLocation =
            file.location ??
            file.locationName ??
            file.site ??
            file?.metadata?.location ??
            file?.metadata?.locationName ??
            file?.metadata?.site ??
            '';

          const normalizedLocation = resolvedLocation ? String(resolvedLocation).trim() : '';

          if (!normalizedLocation) {
            return;
          }

          const safeCompanyId = resolvedCompanyId != null ? String(resolvedCompanyId).trim() : null;
          const companyLabel =
            (safeCompanyId && lookupForLocations[safeCompanyId]?.label) ||
            (fallbackCompanyLabel ? String(fallbackCompanyLabel).trim() : '');

          const locationKey = buildLocationKey(safeCompanyId, normalizedLocation);

          if (!locationKey) {
            return;
          }

          const dedupeKey = `${locationKey}::${normalizedMds}`;

          if (seen.has(dedupeKey)) {
            return;
          }

          seen.add(dedupeKey);

          uniqueMds.push({
            value: normalizedMds,
            label: `${normalizedMds} (${normalizedLocation}${
              companyLabel ? ` - ${companyLabel}` : ''
            })`,
            companyId: safeCompanyId,
            location: normalizedLocation
          });
        });

        setMdsOptions(uniqueMds);
      }
    } catch (err) {
      console.error('Error loading access options:', err);
    } finally {
      setAccessOptionsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch users, stats, and roles in parallel
      const [usersResponse, statsResponse, rolesResponse] = await Promise.all([
        getAllUsers(),
        getUserStats(),
        getAvailableRoles()
      ]);

      // Handle users data
      if (usersResponse && !usersResponse.error && usersResponse.data?.data) {
        const usersData = usersResponse.data.data;
        console.log('Users data array:', usersData);
        setUsers(usersData);
      } else {
        console.log('No users data or error in response');
        setUsers([]);
        setError('Failed to fetch users');
      }

      // Handle stats data
      if (statsResponse && !statsResponse.error && statsResponse.data?.data) {
        setUserStats(statsResponse.data.data);
      }

      // Handle roles data
      if (rolesResponse && !rolesResponse.error && rolesResponse.data?.data) {
        setAvailableRoles(rolesResponse.data.data);
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setUsers([]);
      setError('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    try {
      setError('');
      console.log(isEditMode ? 'Updating user:' : 'Adding user:', newUser);

      if (!newUser.isSuperAdmin) {
        const missing = [];
        if (!newUser.companyAccess.length) missing.push('company');
        if (!newUser.locationAccess.length) missing.push('location');
        if (!newUser.mdsAccess.length) missing.push('MDS');

        if (missing.length) {
          const missingLabel =
            missing.length === 1
              ? missing[0]
              : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
          setError(`Please assign at least one ${missingLabel} access before continuing.`);
          return;
      }
      }
      
      let response;
      const sanitizedCompanyAccess = newUser.isSuperAdmin
        ? []
        : Array.from(
            new Set(
              newUser.companyAccess
                .map(companyId => normalizeAccessIdentifier(companyId))
                .filter(Boolean)
            )
          );

      const normalizedLocationAccessRaw = newUser.isSuperAdmin
        ? []
        : newUser.locationAccess
            .map(normalizeLocationAccessEntry)
            .filter(Boolean)
            .map(entry => {
              const resolvedCompanyId =
                entry.companyId ??
                (sanitizedCompanyAccess.length === 1 ? sanitizedCompanyAccess[0] : null);

              return {
                companyId: normalizeAccessIdentifier(resolvedCompanyId),
                location: normalizeTextValue(entry.location)
              };
            })
            .filter(entry => entry.location);

      if (!newUser.isSuperAdmin && normalizedLocationAccessRaw.some(entry => !entry.companyId)) {
        setError('Please ensure each location is linked to a selected company.');
        return;
      }

      const uniqueLocationAccess = [];
      const locationKeySet = new Set();

      normalizedLocationAccessRaw.forEach(entry => {
        const normalizedCompanyId = entry.companyId != null ? entry.companyId : null;
        const locationKey = `${normalizedCompanyId ?? 'null'}::${entry.location}`;
        if (locationKeySet.has(locationKey)) {
          return;
        }
        locationKeySet.add(locationKey);
        uniqueLocationAccess.push({
          companyId: normalizedCompanyId,
          location: entry.location
        });
      });

      const sanitizedMdsAccess = newUser.isSuperAdmin
        ? []
        : Array.from(
            new Set(
              newUser.mdsAccess
                .map(mdsId => (mdsId != null ? String(mdsId) : null))
                .filter(Boolean)
            )
          );

      const payload = {
        displayName: (newUser.displayName || '').trim(),
        username: (newUser.username || '').trim(),
        role: newUser.role,
        isActive: newUser.isActive,
        isSuperAdmin: newUser.isSuperAdmin,
        companyAccess: sanitizedCompanyAccess,
        locationAccess: uniqueLocationAccess,
        mdsAccess: sanitizedMdsAccess
      };

      if (!isEditMode || newUser.password) {
        payload.password = newUser.password;
      }

      if (isEditMode) {
        // Update existing user
        response = await updateUser(editingUserId, payload);
      } else {
        // Add new user
        response = await createUser(payload);
      }
      
      console.log(isEditMode ? 'Update user response:' : 'Add user response:', response);
      
      if (response && !response.error) {
        // Success - close modal and refresh users list
        setShowAddModal(false);
        setNewUser({ ...INITIAL_USER_STATE });
        setIsEditMode(false);
        setEditingUserId(null);
        
        // Refresh the data
        await fetchData();
        
        // Show success message
        alert(isEditMode ? 'User updated successfully!' : 'User added successfully!');
      } else {
        setError(`Failed to ${isEditMode ? 'update' : 'add'} user: ${response.data?.error || response.data || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} user:`, err);
      setError(`Failed to ${isEditMode ? 'update' : 'add'} user. Please try again.`);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    // Confirm deletion
    const confirmDelete = window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`);
    
    if (!confirmDelete) return;

    try {
      setError('');
      console.log('Deleting user:', userId);
      
      const response = await deleteUser(userId);
      console.log('Delete user response:', response);
      
      if (response && !response.error) {
        // Success - refresh data
        await fetchData();
        alert('User deleted successfully!');
      } else {
        setError(`Failed to delete user: ${response.data?.error || response.data || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    }
  };

  const handleOpenAddModal = () => {
    // Reset form when opening modal for adding
    setError('');
    setIsEditMode(false);
    setEditingUserId(null);
    
    // Clear form with a slight delay to ensure modal is ready
    setTimeout(() => {
      setNewUser({ ...INITIAL_USER_STATE });
    }, 50);
    
    setShowAddModal(true);
  };

  const handleOpenEditModal = (user) => {
    // Populate form with user data for editing
    setNewUser({
      displayName: user.displayName || user.username || '', // Use displayName if available, fallback to username
      username: user.username || '',
      password: '', // Don't populate password for security
      role: user.role || 'user',
      isActive: user.isActive !== undefined ? user.isActive : true,
      isSuperAdmin: Boolean(user.isSuperAdmin),
      companyAccess: Array.isArray(user.companyAccess)
        ? Array.from(
            new Set(
              user.companyAccess
                .map(resolveCompanyId)
                .filter(Boolean)
            )
          )
        : [],
      locationAccess: Array.isArray(user.locationAccess)
        ? (() => {
            const normalized = [];
            const seen = new Set();

            user.locationAccess
              .map(normalizeLocationAccessEntry)
              .forEach(entry => {
                if (!entry || !entry.location) {
                  return;
                }

                const resolvedCompanyId = resolveCompanyId(entry.companyId);
                const key = buildLocationKey(resolvedCompanyId, entry.location);

                if (seen.has(key)) {
                  return;
                }

                seen.add(key);

                normalized.push({
                  companyId: resolvedCompanyId,
                  location: entry.location
                });
              });

            return normalized;
          })()
        : [],
      mdsAccess: Array.isArray(user.mdsAccess)
        ? Array.from(
            new Set(
              user.mdsAccess
                .map(mdsId => (mdsId != null ? String(mdsId) : null))
                .filter(Boolean)
            )
          )
        : []
    });
    setError('');
    setIsEditMode(true);
    setEditingUserId(user._id); // Use _id from the API response
    setShowAddModal(true);
  };

  const getCompanyLabelById = (companyId) => {
    if (!companyId) {
      return '';
    }

    const normalizedId = String(companyId);

    if (companyLookup[normalizedId]?.label) {
      return companyLookup[normalizedId].label;
    }

    const matchedOption = companyOptions.find(option => option.value === normalizedId);
    return matchedOption?.label || normalizedId;
  };

  const resolveCompanyId = (value) => {
    if (value == null) {
      return null;
    }

    const rawValue = String(value).trim();

    if (!rawValue) {
      return null;
    }

    if (companyLookup[rawValue]) {
      return rawValue;
    }

    const matchedByValue = companyOptions.find(option => option.value === rawValue);

    if (matchedByValue) {
      return matchedByValue.value;
    }

    const matchedByLabel = companyOptions.find(option => option.label === rawValue);

    if (matchedByLabel) {
      return matchedByLabel.value;
    }

    return rawValue;
  };

  const formatAccessList = (list, type = 'generic') => {
    if (!Array.isArray(list) || list.length === 0) {
      return 'None';
    }

    if (type === 'location') {
      const formattedLocations = list
        .map(entry => {
          if (!entry) {
            return null;
          }

          if (typeof entry === 'string') {
            return entry;
          }

          const normalizedEntry = normalizeLocationAccessEntry(entry);

          if (!normalizedEntry) {
            return null;
          }

          const companyLabel = normalizedEntry.companyId
            ? getCompanyLabelById(normalizedEntry.companyId)
            : '';

          return companyLabel ? `${normalizedEntry.location} (${companyLabel})` : normalizedEntry.location;
        })
        .filter(Boolean);

      return formattedLocations.length ? formattedLocations.join(', ') : 'None';
    }

    if (type === 'company') {
      const formattedCompanies = list
        .map(entry => {
          if (entry == null) {
            return null;
          }

          const companyId =
            typeof entry === 'string'
              ? entry
              : entry.value ?? entry.id ?? entry.companyId ?? null;

          if (!companyId) {
            return null;
          }

          return getCompanyLabelById(companyId);
        })
        .filter(Boolean);

      return formattedCompanies.length ? formattedCompanies.join(', ') : 'None';
    }

    const formattedItems = list
      .map(item => {
        if (item == null) {
          return null;
        }

        if (typeof item === 'string') {
          return item;
        }

        if (typeof item === 'object' && typeof item.label === 'string') {
          return item.label;
        }

        return String(item);
      })
      .filter(Boolean);

    return formattedItems.length ? formattedItems.join(', ') : 'None';
  };

  useEffect(() => {
    if (!companyOptions.length) {
      return;
    }

    setNewUser(prev => {
      if (
        !Array.isArray(prev.companyAccess) &&
        !Array.isArray(prev.locationAccess)
      ) {
        return prev;
      }

      let hasChanges = false;

      const resolvedCompanyAccess = Array.isArray(prev.companyAccess)
        ? (() => {
            const normalized = [];
            const seen = new Set();

            prev.companyAccess.forEach(value => {
              const resolved = resolveCompanyId(value);

              if (!resolved) {
                return;
              }

              if (!seen.has(resolved)) {
                seen.add(resolved);
                normalized.push(resolved);
              }

              if (!hasChanges && resolved !== value) {
                hasChanges = true;
              }
            });

            if (!hasChanges && normalized.length !== prev.companyAccess.length) {
              hasChanges = true;
            }

            return normalized;
          })()
        : [];

      const resolvedLocationAccess = Array.isArray(prev.locationAccess)
        ? (() => {
            const normalized = [];
            const seen = new Set();

            prev.locationAccess.forEach(entry => {
              const normalizedEntry = normalizeLocationAccessEntry(entry);

              if (!normalizedEntry || !normalizedEntry.location) {
                return;
              }

              const resolvedCompanyId = resolveCompanyId(normalizedEntry.companyId);
              const key = buildLocationKey(resolvedCompanyId, normalizedEntry.location);

              if (seen.has(key)) {
                if (!hasChanges) {
                  hasChanges = true;
                }
                return;
              }

              seen.add(key);

              if (
                !hasChanges &&
                (normalizedEntry.companyId !== resolvedCompanyId ||
                  entry?.companyId !== resolvedCompanyId ||
                  entry?.location !== normalizedEntry.location)
              ) {
                hasChanges = true;
              }

              normalized.push({
                companyId: resolvedCompanyId,
                location: normalizedEntry.location
              });
            });

            if (!hasChanges && normalized.length !== prev.locationAccess.length) {
              hasChanges = true;
            }

            return normalized;
          })()
        : [];

      const resolvedMdsAccess = Array.isArray(prev.mdsAccess)
        ? (() => {
            if (!resolvedLocationAccess.length) {
              if (!hasChanges && prev.mdsAccess.length) {
                hasChanges = true;
              }
              return [];
            }

            const allowedLocationKeys = new Set(
              resolvedLocationAccess
                .map(entry => buildLocationKey(entry.companyId, entry.location))
                .filter(Boolean)
            );

            if (!allowedLocationKeys.size) {
              if (!hasChanges && prev.mdsAccess.length) {
                hasChanges = true;
              }
              return [];
            }

            const filtered = prev.mdsAccess.filter(value => {
              const meta = mdsLookup.get(value);

              if (!meta || !meta.location) {
                return false;
              }

              const locationKey = buildLocationKey(
                meta.companyId != null ? String(meta.companyId) : null,
                meta.location
              );

              return allowedLocationKeys.has(locationKey);
            });

            if (!hasChanges && filtered.length !== prev.mdsAccess.length) {
              hasChanges = true;
            }

            return filtered;
          })()
        : [];

      if (!hasChanges) {
        return prev;
      }

      return {
        ...prev,
        companyAccess: resolvedCompanyAccess,
        locationAccess: resolvedLocationAccess,
        mdsAccess: resolvedMdsAccess
      };
    });
  }, [companyOptions, companyLookup, mdsOptions]);

  const filteredUsers = Array.isArray(users) ? users.filter(user => 
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  return (
    <div className="admin-portal">
      <div className="admin-portal__container">
        <header className="admin-header">
          <div className="admin-header__content">
            <div className="admin-header__left">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="admin-header__back-btn"
              >
                ← Back to Dashboard
              </Button>
              <div className="admin-header__title-section">
                <h1 className="admin-header__title">👥 User Management</h1>
                <p className="admin-header__subtitle">Manage users and their permissions</p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleOpenAddModal}
              className="admin-header__add-btn"
            >
              <span className="add-icon">+</span>
              Add User
            </Button>
          </div>
        </header>

        <main className="admin-content">
          <Card className="admin-card">
            <div className="admin-controls">
              <div className="admin-search">
                <Input
                  type="text"
                  placeholder="🔍 Search users by name, username, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="admin-stats">
                {userStats && (
                  <>
                    <span className="stats-badge">
                      Total: <strong>{userStats.totalUsers || 0}</strong>
                    </span>
                    <span className="stats-badge">
                      Active: <strong>{userStats.activeUsers || 0}</strong>
                    </span>
                    <span className="stats-badge">
                      Admins: <strong>{userStats.adminUsers || 0}</strong>
                    </span>
                    <span className="stats-badge">
                      Users: <strong>{userStats.regularUsers || 0}</strong>
                    </span>
                  </>
                )}
              </div>
            </div>

            {error && (
              <div className="error-banner">
                ⚠️ {error}
              </div>
            )}

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading users...</p>
              </div>
            ) : (
              <div className="users-grid">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user, index) => (
                    <div key={user._id || index} className="user-card">
                      <div className="user-avatar">
                        {user.displayName?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="user-info">
                        <h3 className="user-name">{user.displayName || user.username || 'Unknown User'}</h3>
                        <p className="user-email">{user.username}</p>
                        <p className="user-status">
                          Status: <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                        <span className={`user-role ${user.role || 'user'}`}>
                          {user.role || 'User'}
                        </span>
                      </div>
                      <div className="user-access-summary">
                        <p className="user-access-row">
                          <span className="user-access-label">Super Admin:</span>{' '}
                          <span className="user-access-value">{user.isSuperAdmin ? 'Yes' : 'No'}</span>
                        </p>
                        {!user.isSuperAdmin && (
                          <>
                            <p className="user-access-row">
                              <span className="user-access-label">Companies:</span>{' '}
                              <span className="user-access-value">
                                {formatAccessList(user.companyAccess, 'company')}
                              </span>
                            </p>
                            <p className="user-access-row">
                              <span className="user-access-label">Locations:</span>{' '}
                              <span className="user-access-value">
                                {formatAccessList(user.locationAccess, 'location')}
                              </span>
                            </p>
                            <p className="user-access-row">
                              <span className="user-access-label">MDS:</span>{' '}
                              <span className="user-access-value">
                                {formatAccessList(user.mdsAccess)}
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                      <div className="user-actions">
                        <Button 
                          variant="outline" 
                          size="small"
                          onClick={() => handleOpenEditModal(user)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="small" 
                          className="delete-btn"
                          onClick={() => handleDeleteUser(user._id, user.displayName || user.username)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">👤</div>
                    <h3>No users found</h3>
                    <p>
                      {searchTerm 
                        ? 'No users match your search criteria' 
                        : 'No users found. Add your first user to get started.'
                      }
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card>
        </main>

        {/* Add User Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{isEditMode ? '✏️ Edit User' : '➕ Add New User'}</h2>
                <button 
                  className="modal-close"
                  onClick={() => setShowAddModal(false)}
                >
                  ✕
                </button>
              </div>
              <form 
                onSubmit={(e) => e.preventDefault()} 
                autoComplete="off" 
                className="add-user-form"
                key={showAddModal ? 'modal-open' : 'modal-closed'}
              >
                <div className="modal-body">
                <div className="form-field">
                  <Input
                    label="Display Name"
                    type="text"
                    name="admin-display-name"
                    placeholder="Enter user's full name"
                    value={newUser.displayName}
                    onChange={(e) => setNewUser({...newUser, displayName: e.target.value})}
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="form-field">
                  <Input
                    label="Username (Email)"
                    type="email"
                    name="admin-username"
                    placeholder="Enter user's email address"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="form-field">
                  <Input
                    label={isEditMode ? "Password (leave empty to keep current)" : "Password"}
                    type="password"
                    name="admin-password"
                    placeholder={isEditMode ? "Enter new password (optional)" : "Enter secure password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    autoComplete="new-password"
                    required={!isEditMode}
                  />
                </div>
                <div className="form-field">
                  <label className="field-label">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="role-select"
                  >
                    {availableRoles.length > 0 ? (
                      availableRoles.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="form-field">
                  <label className="field-label checkbox-label">
                    <input
                      type="checkbox"
                      checked={newUser.isSuperAdmin}
                      onChange={(e) =>
                        setNewUser(prev => ({
                          ...prev,
                          isSuperAdmin: e.target.checked,
                          companyAccess: e.target.checked ? [] : prev.companyAccess,
                          locationAccess: e.target.checked ? [] : prev.locationAccess,
                          mdsAccess: e.target.checked ? [] : prev.mdsAccess
                        }))
                      }
                    />
                    Super Admin (full access)
                  </label>
                  <p className="field-helper">
                    Super admins automatically receive access to all companies, locations, and MDS records.
                  </p>
                </div>
                {!newUser.isSuperAdmin && (
                  <>
                    <div className="form-field">
                      <label className="field-label">Company Access</label>
                      <Select
                        isMulti
                        isDisabled={accessOptionsLoading}
                        isLoading={accessOptionsLoading}
                        closeMenuOnSelect={false}
                        options={companyOptions}
                        value={companyOptions.filter(option => newUser.companyAccess.includes(option.value))}
                        onChange={(selected) =>
                          setNewUser(prev => {
                            const normalizedCompanyIds = Array.isArray(selected)
                              ? Array.from(
                                  new Set(
                                    selected
                                      .map(option =>
                                        option?.value != null ? String(option.value).trim() : null
                                      )
                                      .filter(value => value)
                                  )
                                )
                              : [];

                            if (!normalizedCompanyIds.length) {
                              return {
                                ...prev,
                                companyAccess: [],
                                locationAccess: [],
                                mdsAccess: []
                              };
                            }

                            const normalizedCompanyIdSet = new Set(normalizedCompanyIds);

                            const adjustedLocationAccess = Array.isArray(prev.locationAccess)
                              ? (() => {
                                  const deduped = [];
                                  const seen = new Set();

                                  prev.locationAccess
                                    .map(normalizeLocationAccessEntry)
                                    .filter(Boolean)
                                    .forEach(entry => {
                                      const candidateCompanyId = entry.companyId
                                        ? String(entry.companyId)
                                        : normalizedCompanyIds.length === 1
                                          ? normalizedCompanyIds[0]
                                          : null;

                                      if (!candidateCompanyId || !normalizedCompanyIdSet.has(candidateCompanyId)) {
                                        return;
                                      }

                                      const key = buildLocationKey(candidateCompanyId, entry.location);
                                      if (seen.has(key)) {
                                        return;
                                      }
                                      seen.add(key);

                                      deduped.push({
                                        companyId: candidateCompanyId,
                                        location: entry.location
                                      });
                                    });

                                  return deduped;
                                })()
                              : [];

                            const allowedLocationKeys = new Set(
                              adjustedLocationAccess
                                .map(entry => buildLocationKey(entry.companyId, entry.location))
                                .filter(Boolean)
                            );

                            const adjustedMdsAccess =
                              allowedLocationKeys.size && Array.isArray(prev.mdsAccess)
                                ? prev.mdsAccess.filter(value => {
                                    const meta = mdsLookup.get(value);

                                    if (!meta || !meta.location) {
                                      return false;
                                    }

                                    const key = buildLocationKey(
                                      meta.companyId != null ? String(meta.companyId) : null,
                                      meta.location
                                    );

                                    return allowedLocationKeys.has(key);
                                  })
                                : [];

                            return {
                              ...prev,
                              companyAccess: normalizedCompanyIds,
                              locationAccess: adjustedLocationAccess,
                              mdsAccess: adjustedMdsAccess
                            };
                          })
                        }
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder="Select companies..."
                        noOptionsMessage={() =>
                          accessOptionsLoading ? 'Loading companies...' : 'No companies available'
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label className="field-label">Location Access</label>
                      <Select
                        isMulti
                        isDisabled={accessOptionsLoading || !newUser.companyAccess.length}
                        isLoading={accessOptionsLoading}
                        closeMenuOnSelect={false}
                        options={availableLocationOptions}
                        value={availableLocationOptions.filter(option => selectedLocationKeySet.has(option.value))}
                        onChange={(selected) =>
                          setNewUser(prev => {
                            if (!Array.isArray(selected)) {
                              return {
                                ...prev,
                                locationAccess: [],
                                mdsAccess: []
                              };
                            }

                            const seen = new Set();
                            const normalizedSelections = [];

                            selected.forEach(option => {
                              if (!option || !option.location) {
                                return;
                              }

                              const inferredCompanyId =
                                option.companyId ??
                                (prev.companyAccess.length === 1 ? prev.companyAccess[0] : null);

                              const normalizedCompanyId =
                                inferredCompanyId != null ? String(inferredCompanyId) : null;

                              if (!normalizedCompanyId) {
                                return;
                              }

                              const key = buildLocationKey(normalizedCompanyId, option.location);

                              if (seen.has(key)) {
                                return;
                              }
                              seen.add(key);

                              normalizedSelections.push({
                                companyId: normalizedCompanyId,
                                location: option.location
                              });
                            });

                            const allowedLocationKeys = new Set(
                              normalizedSelections
                                .map(entry => buildLocationKey(entry.companyId, entry.location))
                                .filter(Boolean)
                            );

                            const filteredMdsAccess =
                              allowedLocationKeys.size && Array.isArray(prev.mdsAccess)
                                ? prev.mdsAccess.filter(value => {
                                    const meta = mdsLookup.get(value);

                                    if (!meta || !meta.location) {
                                      return false;
                                    }

                                    const locationKey = buildLocationKey(
                                      meta.companyId != null ? String(meta.companyId) : null,
                                      meta.location
                                    );

                                    return allowedLocationKeys.has(locationKey);
                                  })
                                : [];

                            return {
                              ...prev,
                              locationAccess: normalizedSelections,
                              mdsAccess: filteredMdsAccess
                            };
                          })
                        }
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder={
                          newUser.companyAccess.length
                            ? 'Select locations...'
                            : 'Select a company first'
                        }
                        noOptionsMessage={() => {
                          if (accessOptionsLoading) {
                            return 'Loading locations...';
                          }

                          if (!newUser.companyAccess.length) {
                            return 'Select a company to view locations';
                          }

                          return 'No locations available';
                        }}
                      />
                    </div>
                    <div className="form-field">
                      <label className="field-label">MDS Access</label>
                      <Select
                        isMulti
                        isDisabled={accessOptionsLoading || !newUser.locationAccess.length}
                        isLoading={accessOptionsLoading}
                        closeMenuOnSelect={false}
                        options={availableMdsOptions}
                        value={availableMdsOptions.filter(option => newUser.mdsAccess.includes(option.value))}
                        onChange={(selected) =>
                          setNewUser(prev => ({
                            ...prev,
                            mdsAccess: Array.isArray(selected) ? selected.map(option => option.value) : []
                          }))
                        }
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder={
                          newUser.locationAccess.length
                            ? 'Select MDS numbers...'
                            : 'Select locations first'
                        }
                        noOptionsMessage={() => {
                          if (accessOptionsLoading) {
                            return 'Loading MDS numbers...';
                          }

                          if (!newUser.locationAccess.length) {
                            return 'Select locations to view MDS numbers';
                          }

                          return 'No MDS numbers available for selected locations';
                        }}
                      />
                    </div>
                  </>
                )}
                {isEditMode && (
                  <div className="form-field">
                    <label className="field-label">Status</label>
                    <select
                      value={newUser.isActive}
                      onChange={(e) => setNewUser({...newUser, isActive: e.target.value === 'true'})}
                      className="role-select"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                )}
                </div>
                <div className="modal-footer">
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAddUser}
                  disabled={
                    !newUser.displayName ||
                    !newUser.username ||
                    (!isEditMode && !newUser.password) ||
                    (!newUser.isSuperAdmin && (
                      !newUser.companyAccess.length ||
                      !newUser.locationAccess.length ||
                      !newUser.mdsAccess.length
                    ))
                  }
                >
                  {isEditMode ? 'Update User' : 'Add User'}
                </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddUser;
